/**
 * Helpers for flushing queued `chat_add` jobs as one batch on reconnect.
 */
import { getOperationName } from "./cache";

/**
 * True when a sync job is a single chat_add mutation.
 *
 * @param {Object} job Queue record.
 * @returns {boolean}
 */
export const isChatAddJob = (job) => job?.kind === "gql" && getOperationName(job.query) === "chat_add";

/**
 * True when a queued job is presence/typing and should be dropped, not replayed.
 *
 * @param {Object} job Queue record.
 * @returns {boolean}
 */
export const isEphemeralJob = (job) => {
  const name = getOperationName(job?.query);
  return name === "presence_ping" || name === "presence_leave" || name === "chat_typing";
};

/**
 * Collects a consecutive run of chat_add jobs starting at `start`.
 *
 * @param {Array<Object>} jobs Queue in FIFO order.
 * @param {number} start Index of the first chat_add job.
 * @param {Set<*>} [skipIds] Job ids already sent in this flush.
 * @returns {Array<Object>}
 */
export const takeConsecutiveChatJobs = (jobs, start, skipIds = new Set()) => {
  const run = [];
  for (let i = start; i < jobs.length; i += 1) {
    const job = jobs[i];
    if (!isChatAddJob(job) || skipIds.has(job.id)) break;
    run.push(job);
  }
  return run;
};

/**
 * Maps queued chat_add variables onto ChatAddInput.
 *
 * @param {Array<Object>} jobs chat_add queue records.
 * @returns {Array<Object>}
 */
export const chatJobsToInputs = (jobs) =>
  (jobs || []).map((job) => ({
    event_id: job.variables?.event_id,
    text: job.variables?.text || "",
    urls: Array.isArray(job.variables?.urls) ? job.variables.urls : [],
    created: job.variables?.created || undefined,
  }));
